import { useRef, useState } from "react";
import { UploadCloud, Loader2, X, Star } from "lucide-react";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function MediaUpload({ propertyId, category = "gallery", multiple = true, gallery = [], cover, onChange, coverPath, onSetCover }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const added = [];
    for (const file of Array.from(files)) {
      const form = new FormData();
      form.append("category", category);
      form.append("file", file);
      try {
        const { data } = await api.post(`/properties/${propertyId}/media`, form);
        added.push(data.media);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
      }
    }
    setUploading(false);
    if (added.length) {
      onChange([...gallery, ...added]);
      toast.success(`${added.length} photo${added.length > 1 ? "s" : ""} uploaded`);
    }
  };

  const remove = (id) => onChange(gallery.filter((g) => g.id !== id));

  return (
    <div data-testid="media-upload">
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors"
        data-testid="media-dropzone"
      >
        {uploading ? (
          <Loader2 className="w-6 h-6 mx-auto text-blue-500 animate-spin" />
        ) : (
          <UploadCloud className="w-6 h-6 mx-auto text-slate-400" />
        )}
        <p className="mt-2 text-sm text-slate-600 font-medium">Drag & drop or click to upload</p>
        <p className="text-xs text-slate-400">JPG, PNG or WebP · optimized automatically</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        data-testid="media-file-input"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {gallery.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mt-4">
          {gallery.map((g) => (
            <div key={g.id} className="relative group rounded-lg overflow-hidden border border-slate-100 aspect-square">
              <img src={mediaUrl(g.url || g.storage_path)} alt={g.alt || ""} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => remove(g.id)}
                data-testid={`media-remove-${g.id}`}
                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              {onSetCover && (
                <button
                  type="button"
                  onClick={() => onSetCover(g.storage_path)}
                  className={`absolute bottom-1 left-1 rounded-full p-1 transition-opacity ${coverPath === g.storage_path ? "bg-blue-600 text-white" : "bg-black/50 text-white opacity-0 group-hover:opacity-100"}`}
                  title="Set as cover"
                >
                  <Star className="w-3.5 h-3.5" fill={coverPath === g.storage_path ? "currentColor" : "none"} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
